import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { UtensilsCrossed, ChevronDown, ChevronUp } from "lucide-react";

const Chips = ({ items, tone }) =>
  items?.length ? (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {items.map((x, i) => (
        <span key={i} className={`text-[11px] px-2 py-0.5 rounded-sm border ${tone}`}>{x}</span>
      ))}
    </div>
  ) : (
    <div className="text-zinc-600 text-sm mt-1">—</div>
  );

export const ClientNutritionCard = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [showPlan, setShowPlan] = useState(false);

  useEffect(() => {
    api.get(`/coaching/clients/${clientId}/nutrition`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId]);

  if (!data) return null;
  const { diet = {}, macros, daily_plan: daily, weekly_plan: weekly } = data;
  const meals = daily?.plan?.meals || [];

  return (
    <div className="obs-card" data-testid="client-nutrition-card">
      <div className="flex items-center gap-3 mb-4">
        <UtensilsCrossed className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">Nutrition</div>
          <h3 className="font-heading text-2xl uppercase">Diet, macros & meal plan</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="client-diet-prefs">
        <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
          <div className="overline">Diet</div>
          <div className="font-heading text-sm uppercase mt-1">{diet.dietary_preference || "—"}</div>
        </div>
        <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
          <div className="overline">Allergies</div>
          <Chips items={diet.allergies} tone="border-red-500/40 text-red-300" />
        </div>
        <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
          <div className="overline">Likes</div>
          <Chips items={diet.food_likes} tone="border-emerald-500/40 text-emerald-300" />
        </div>
        <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
          <div className="overline">Dislikes</div>
          <Chips items={diet.food_dislikes} tone="border-obsidian-500 text-zinc-300" />
        </div>
      </div>

      {macros ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="client-macros">
          <div className="stat-tile"><div className="overline">kcal target</div><div className="font-data text-2xl">{macros.calories}</div></div>
          <div className="stat-tile"><div className="overline">Protein</div><div className="font-data text-2xl text-blaze">{macros.protein_g}g</div></div>
          <div className="stat-tile"><div className="overline">Carbs</div><div className="font-data text-2xl">{macros.carbs_g}g</div></div>
          <div className="stat-tile"><div className="overline">Fat</div><div className="font-data text-2xl">{macros.fat_g}g</div></div>
        </div>
      ) : (
        <p className="text-zinc-500 text-sm mb-4">No macros yet — client hasn't completed onboarding.</p>
      )}

      {daily ? (
        <div className="border border-obsidian-600 rounded-sm bg-obsidian-900/40">
          <button
            type="button"
            data-testid="client-mealplan-toggle"
            onClick={() => setShowPlan((s) => !s)}
            className="w-full flex items-center justify-between px-3 py-2.5 text-left"
          >
            <div>
              <div className="overline">Current meal plan</div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {meals.length} meals · generated {new Date(daily.created_at).toLocaleDateString()}
                {weekly && <span className="text-zinc-500"> · also has a 7-day plan ({new Date(weekly.created_at).toLocaleDateString()})</span>}
              </div>
            </div>
            {showPlan ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
          </button>
          {showPlan && (
            <div className="px-3 pb-3 space-y-2" data-testid="client-mealplan-detail">
              {meals.map((m, i) => (
                <div key={i} className="border-t border-obsidian-600/50 pt-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-heading text-sm uppercase truncate">{m.name}</div>
                    <div className="font-data text-xs text-zinc-400 shrink-0">
                      {m.calories} kcal · <span className="text-blaze">{m.protein_g}P</span> {m.carbs_g}C {m.fat_g}F
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {(m.items || []).map((it) => it.food).join(", ")}
                  </div>
                </div>
              ))}
              {daily.plan?.totals && (
                <div className="border-t border-obsidian-600/50 pt-2 text-xs text-zinc-400 font-data">
                  Day total: {daily.plan.totals.calories} kcal · {daily.plan.totals.protein_g}g P · {daily.plan.totals.carbs_g}g C · {daily.plan.totals.fat_g}g F
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-zinc-500 text-sm" data-testid="client-no-mealplan">No meal plan generated yet.</p>
      )}
    </div>
  );
};

export default ClientNutritionCard;
